// Phase 8 D2 — top-of-page filter bar for the Overview dashboard.
//
// Controlled component: every interaction emits `onChange(patch)` with the
// minimum delta the parent should merge into URL search params, OR
// `onReset()` to clear back to schema defaults. State internal to this
// component is limited to typeahead text + dropdown-open booleans — the
// canonical filter state lives in the URL via `useDashboardFilters`.
//
// Visual conventions mirror `customers/index.tsx` and `invoices/index.tsx`:
// 36px controls, brand-indigo (#4f46e5) focus rings, neutral grays for
// inactive borders, brand-tinted treatment for active filters. shadcn
// primitives like Select/Combobox/Sheet are NOT installed in this codebase
// per the dispatch spec; controls are native HTML elements styled with
// Tailwind to match the existing page-filter row pattern.
//
// 4-state coverage:
//   - Loaded: all controls interactive (default).
//   - Loading: customer-typeahead source list shows "Loading…" placeholder
//     while `trpc.customers.list.useQuery` is fetching first-page customers;
//     other controls render fully.
//   - Empty: N/A — filter row is always renderable.
//   - Error: N/A here — Zod-parse failures on the URL surface to the route
//     error boundary, not this component.

import { trpc } from "@/lib/trpc";
import type {
	BillingModeFilter,
	CustomerStatusFilter,
	DashboardFilters as DashboardFiltersValue,
	PayrollProvider,
	TimeRangePreset,
} from "@payrollmart-invoice/shared";
import { RotateCcw, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";

const TIME_RANGE_OPTIONS: Array<{ value: TimeRangePreset; label: string }> = [
	{ value: "mtd", label: "Month to date" },
	{ value: "qtd", label: "Quarter to date" },
	{ value: "ytd", label: "Year to date" },
	{ value: "last_30d", label: "Last 30 days" },
	{ value: "last_90d", label: "Last 90 days" },
	{ value: "last_12mo", label: "Last 12 months" },
	{ value: "last_24mo", label: "Last 24 months" },
	{ value: "custom", label: "Custom" },
];

const PROVIDER_OPTIONS: PayrollProvider[] = ["ADP", "Paylocity", "SurePayroll", "Gusto"];

const BILLING_MODE_OPTIONS: Array<{ value: BillingModeFilter; label: string }> = [
	{ value: "per_pay", label: "Per pay" },
	{ value: "monthly", label: "Monthly" },
	{ value: "pass_through", label: "Pass-through" },
];

const CUSTOMER_STATUS_OPTIONS: Array<{ value: CustomerStatusFilter; label: string }> = [
	{ value: "active", label: "Active" },
	{ value: "inactive", label: "Inactive" },
	{ value: "all", label: "All" },
];

export type DashboardFiltersProps = {
	value: DashboardFiltersValue;
	onChange: (patch: Partial<DashboardFiltersValue>) => void;
	onReset: () => void;
};

export function DashboardFilters({ value, onChange, onReset }: DashboardFiltersProps) {
	return (
		<div
			className="flex flex-wrap items-center gap-3 rounded-lg border border-[#e5e7eb] bg-white p-3"
			data-testid="dashboard-filters"
		>
			<TimeRangeControl value={value} onChange={onChange} />
			<TimeBasisToggle value={value} onChange={onChange} />
			<ProviderChips value={value} onChange={onChange} />
			<BillingModeChips value={value} onChange={onChange} />
			<CustomerStatusControl value={value} onChange={onChange} />
			<CustomerTypeahead value={value} onChange={onChange} />
			<button
				type="button"
				onClick={onReset}
				className="ml-auto inline-flex h-9 items-center gap-1.5 rounded border border-transparent px-2 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f8f9fb] hover:text-[#1e1e20]"
				data-testid="dashboard-filters-reset"
			>
				<RotateCcw className="h-3.5 w-3.5" strokeWidth={1.5} />
				Reset
			</button>
		</div>
	);
}

function TimeRangeControl({
	value,
	onChange,
}: {
	value: DashboardFiltersValue;
	onChange: (patch: Partial<DashboardFiltersValue>) => void;
}) {
	// Custom range: only emit `onChange` for the date inputs once both ends
	// are filled. Until then, the URL retains the previous valid window.
	const showCustom = value.timeRange === "custom";
	const [pendingStart, setPendingStart] = useState(value.customStart ?? "");
	const [pendingEnd, setPendingEnd] = useState(value.customEnd ?? "");

	return (
		<div className="flex flex-col gap-1.5">
			<select
				value={value.timeRange}
				onChange={(e) => {
					const next = e.target.value as TimeRangePreset;
					if (next === "custom") {
						onChange({ timeRange: "custom" });
						return;
					}
					// Leaving custom: clear the lingering custom dates so the
					// schema-refine doesn't trip on a half-set state next time
					// the user picks Custom again.
					onChange({ timeRange: next, customStart: undefined, customEnd: undefined });
				}}
				className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				aria-label="Time range"
				data-testid="dashboard-filters-time-range"
			>
				{TIME_RANGE_OPTIONS.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>
			{showCustom && (
				<div className="flex items-center gap-2" data-testid="dashboard-filters-custom-range">
					<input
						type="date"
						value={pendingStart}
						onChange={(e) => {
							const next = e.target.value;
							setPendingStart(next);
							if (next && pendingEnd) {
								onChange({ customStart: next, customEnd: pendingEnd });
							}
						}}
						className="h-9 rounded border border-[#d1d5db] bg-white px-2 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						aria-label="Custom start date"
						data-testid="dashboard-filters-custom-start"
					/>
					<span className="text-xs text-[#6b7280]">to</span>
					<input
						type="date"
						value={pendingEnd}
						onChange={(e) => {
							const next = e.target.value;
							setPendingEnd(next);
							if (pendingStart && next) {
								onChange({ customStart: pendingStart, customEnd: next });
							}
						}}
						className="h-9 rounded border border-[#d1d5db] bg-white px-2 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
						aria-label="Custom end date"
						data-testid="dashboard-filters-custom-end"
					/>
				</div>
			)}
		</div>
	);
}

function TimeBasisToggle({
	value,
	onChange,
}: {
	value: DashboardFiltersValue;
	onChange: (patch: Partial<DashboardFiltersValue>) => void;
}) {
	const isPeriodEnd = value.timeBasis === "period_end";
	const label = isPeriodEnd
		? "Showing by billing period — switch to invoice date"
		: "Showing by invoice date — switch to billing period";
	return (
		<button
			type="button"
			onClick={() => onChange({ timeBasis: isPeriodEnd ? "created_at" : "period_end" })}
			className="text-xs font-medium text-[#4f46e5] underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
			data-testid="dashboard-filters-time-basis"
		>
			{label}
		</button>
	);
}

function ProviderChips({
	value,
	onChange,
}: {
	value: DashboardFiltersValue;
	onChange: (patch: Partial<DashboardFiltersValue>) => void;
}) {
	const toggle = (provider: PayrollProvider) => {
		const next = value.providers.includes(provider)
			? value.providers.filter((p) => p !== provider)
			: [...value.providers, provider];
		onChange({ providers: next });
	};
	return (
		<div className="flex items-center gap-1.5" data-testid="dashboard-filters-providers">
			{PROVIDER_OPTIONS.map((p) => {
				const active = value.providers.includes(p);
				return (
					<button
						key={p}
						type="button"
						onClick={() => toggle(p)}
						className={`inline-flex h-9 items-center rounded border px-3 text-sm font-medium transition-colors ${
							active
								? "border-[#4f46e5] bg-[#eef2ff] text-[#4f46e5]"
								: "border-[#d1d5db] bg-white text-[#6b7280] hover:bg-[#f8f9fb]"
						}`}
						aria-pressed={active}
						data-testid={`dashboard-filters-provider-${p}`}
					>
						{p}
					</button>
				);
			})}
		</div>
	);
}

function BillingModeChips({
	value,
	onChange,
}: {
	value: DashboardFiltersValue;
	onChange: (patch: Partial<DashboardFiltersValue>) => void;
}) {
	const toggle = (mode: BillingModeFilter) => {
		const next = value.billingModes.includes(mode)
			? value.billingModes.filter((m) => m !== mode)
			: [...value.billingModes, mode];
		onChange({ billingModes: next });
	};
	return (
		<div className="flex items-center gap-1.5" data-testid="dashboard-filters-billing-modes">
			{BILLING_MODE_OPTIONS.map((opt) => {
				const active = value.billingModes.includes(opt.value);
				return (
					<button
						key={opt.value}
						type="button"
						onClick={() => toggle(opt.value)}
						className={`inline-flex h-9 items-center rounded border px-3 text-sm font-medium transition-colors ${
							active
								? "border-[#4f46e5] bg-[#eef2ff] text-[#4f46e5]"
								: "border-[#d1d5db] bg-white text-[#6b7280] hover:bg-[#f8f9fb]"
						}`}
						aria-pressed={active}
						data-testid={`dashboard-filters-billing-mode-${opt.value}`}
					>
						{opt.label}
					</button>
				);
			})}
		</div>
	);
}

function CustomerStatusControl({
	value,
	onChange,
}: {
	value: DashboardFiltersValue;
	onChange: (patch: Partial<DashboardFiltersValue>) => void;
}) {
	return (
		<select
			value={value.customerStatus}
			onChange={(e) => onChange({ customerStatus: e.target.value as CustomerStatusFilter })}
			className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
			aria-label="Customer status"
			data-testid="dashboard-filters-customer-status"
		>
			{CUSTOMER_STATUS_OPTIONS.map((opt) => (
				<option key={opt.value} value={opt.value}>
					{opt.label}
				</option>
			))}
		</select>
	);
}

type CustomerOption = { id: string; name: string };

function CustomerTypeahead({
	value,
	onChange,
}: {
	value: DashboardFiltersValue;
	onChange: (patch: Partial<DashboardFiltersValue>) => void;
}) {
	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	// Tracks the dropdown panel so the input's onBlur can decide whether the
	// next-focused element is still inside the typeahead (in which case we
	// keep the panel open). Replaces the prior 150ms blur-defer hack — that
	// pattern ate keyboard focus moves and was the M-4 review finding.
	const panelRef = useRef<HTMLDivElement | null>(null);

	// Reuse the existing customers.list endpoint per AC-S-3 (no new endpoint).
	// `payrollCustomer: true` matches the dashboard's default scope so the
	// typeahead surfaces the same population the rest of the page filters on.
	const { data, isLoading } = trpc.customers.list.useQuery({
		payrollCustomer: true,
		page: 1,
		pageSize: 100,
	});

	const allCustomers: CustomerOption[] = useMemo(
		() => (data?.items ?? []).map((c) => ({ id: c.id, name: c.name })),
		[data],
	);

	const selected = useMemo(
		() => allCustomers.filter((c) => value.customerIds.includes(c.id)),
		[allCustomers, value.customerIds],
	);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return allCustomers.filter((c) => !value.customerIds.includes(c.id));
		return allCustomers.filter(
			(c) => !value.customerIds.includes(c.id) && c.name.toLowerCase().includes(q),
		);
	}, [allCustomers, query, value.customerIds]);

	const removeCustomer = (id: string) => {
		onChange({ customerIds: value.customerIds.filter((existing) => existing !== id) });
	};

	const addCustomer = (id: string) => {
		if (value.customerIds.includes(id)) return;
		onChange({ customerIds: [...value.customerIds, id] });
		setQuery("");
	};

	return (
		<div className="relative" data-testid="dashboard-filters-customers">
			<div className="flex items-center gap-1.5">
				{selected.map((c) => (
					<span
						key={c.id}
						className="inline-flex h-9 items-center gap-1 rounded border border-[#4f46e5] bg-[#eef2ff] px-2 text-sm font-medium text-[#4f46e5]"
						data-testid={`dashboard-filters-customer-chip-${c.id}`}
					>
						{c.name}
						<button
							type="button"
							onClick={() => removeCustomer(c.id)}
							className="rounded-full p-0.5 hover:bg-[#4f46e5]/10"
							aria-label={`Remove ${c.name}`}
						>
							<X className="h-3 w-3" strokeWidth={2} />
						</button>
					</span>
				))}
				<input
					type="text"
					value={query}
					onChange={(e) => {
						setQuery(e.target.value);
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					onBlur={(e) => {
						// Keep the panel open if focus moves into it (keyboard
						// Tab → option, or pointerdown on a list item). Closes
						// only when focus leaves the typeahead entirely. The
						// prior 150ms blur-defer swallowed keyboard activations
						// (M-4 review finding).
						const next = e.relatedTarget as Node | null;
						if (!panelRef.current || !next || !panelRef.current.contains(next)) {
							setOpen(false);
						}
					}}
					placeholder={isLoading ? "Loading…" : "Filter customers…"}
					disabled={isLoading}
					className="h-9 w-48 rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] placeholder:text-[#9ca3af] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 disabled:opacity-50"
					aria-label="Customers"
					data-testid="dashboard-filters-customer-input"
				/>
			</div>
			{open && filtered.length > 0 && (
				<div
					ref={panelRef}
					className="absolute left-0 top-full z-10 mt-1 max-h-60 w-64 overflow-y-auto rounded border border-[#e5e7eb] bg-white shadow-md"
					data-testid="dashboard-filters-customer-options"
				>
					<ul>
						{filtered.slice(0, 12).map((c) => (
							<li key={c.id}>
								<button
									type="button"
									onMouseDown={(e) => {
										// onMouseDown so pointer activations fire
										// before the input's onBlur runs.
										e.preventDefault();
										addCustomer(c.id);
									}}
									onClick={() => {
										// onClick covers keyboard activation
										// (Tab to option + Enter/Space) — that
										// path doesn't fire onMouseDown.
										addCustomer(c.id);
									}}
									className="block w-full px-3 py-2 text-left text-sm text-[#1e1e20] hover:bg-[#f8f9fb]"
									data-testid={`dashboard-filters-customer-option-${c.id}`}
								>
									{c.name}
								</button>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}
